<script>
    import {getContext} from 'svelte';
    import {bxSubmit} from "../Methods.svelte";
    import {form} from "../stores/store";
    import ContactTabs from "./ContactTabs.svelte";
    import Captcha from "./Captcha.svelte";

    const {close} = getContext('simple-modal');

    let isLoading = false;
    let formElement;
    let errors = {};
    let consent = false;
    let consentError = '';
    let captchaToken = '';
    let captchaError = '';

    // Phone and e-mail live inside the tab widget: the component filters PHONE and
    // EMAIL out of $form.fields, so this is their only source in the form.
    let contactTabs;

    async function submit(event) {
        if (!validate()) {
            return;
        }

        let result = await bxSubmit(
            $form.iblock,
            $form.event,
            [
                ...$form.fields,
                ...$form.defaultData,
                ...contactTabs.getPayload(),
                {code: 'PRIVACY_CONSENT', value: consent ? 'Y' : 'N'},
            ],
            captchaToken
        );

        $form.success = true;
        form.set($form);
    }

    function validate() {
        let dummyOk = true;
        const dummyInput = formElement?.querySelector('#dummy');
        if (dummyInput && dummyInput.value !== '') {
            dummyOk = false;
        }

        $form.fields.forEach((field) => {
            if (field.is_required === true) {
                const value = field.value ?? '';
                if (String(value).trim() === '') {
                    errors[field.code] = 'Заполните поле';
                } else {
                    errors[field.code] = '';
                }
            } else {
                errors[field.code] = '';
            }
        });

        if (consent !== true) {
            consentError = 'Подтвердите согласие';
        } else {
            consentError = '';
        }

        if (!captchaToken) {
            captchaError = 'Пройдите проверку «Я не робот»';
        } else {
            captchaError = '';
        }

        errors = errors;

        // Called unconditionally, before the combined result: the widget paints its own
        // error, so short-circuiting it would leave the client without the message.
        const contactOk = contactTabs?.validate() === true;

        const hasFieldErrors = Object.values(errors).some((msg) => msg);
        return dummyOk && !hasFieldErrors && contactOk && consent === true && !!captchaToken;
    }

    function onCaptchaToken(event) {
        captchaToken = event.detail || '';
        if (captchaToken) captchaError = '';
    }

    function clearError(code) {
        if (errors[code]) {
            errors[code] = '';
            errors = errors;
        }
    }
</script>

<form bind:this={formElement} on:submit|preventDefault|stopPropagation={submit} action="javascript:void()" novalidate>
    <input type="text" id="dummy" name="dummy" value=""
           style="height: 0;border: none;outline: none;padding: 0;margin: 0">
    <div class="form-container">
        {#each $form.defaultData as {code, value}(code)}
            <input type="hidden" id="{code}" name="{code}" value="{value}">
        {/each}

        {#each $form.fields as field (field.code)}
            <label for="{field.code}">
                {field.name}
                {#if field.is_required}<span class="required">*</span>{/if}: </label>

            {#if field.code === 'QUESTION'}
                <div class="field-cell">
                    <textarea
                            id="{field.code}"
                            name="{field.code}"
                            rows="5"
                            cols="50"
                            bind:value={field.value}
                            class:has-error={errors[field.code]}
                            on:input={() => clearError(field.code)}/>
                    {#if errors[field.code]}<span class="field-error">{errors[field.code]}</span>{/if}
                </div>
            {:else}
                <div class="field-cell">
                    <input type="text"
                           id="{field.code}"
                           name="{field.code}"
                           bind:value={field.value}
                           class:has-error={errors[field.code]}
                           on:input={() => clearError(field.code)}/>
                    {#if errors[field.code]}<span class="field-error">{errors[field.code]}</span>{/if}
                </div>
            {/if}
        {/each}

        <div class="contact-method-cell">
            <ContactTabs bind:this={contactTabs} idPrefix="item_ask"/>
        </div>

        <div class="privacy-consent-group form-consent">
            <div class="privacy-consent-item">
                <label class="privacy-consent-label" for="item_ask_consent_pdn">
                    <input type="checkbox" id="item_ask_consent_pdn" bind:checked={consent}
                           on:change={() => { if (consent) consentError = ''; }}>
                    <span class="privacy-consent-text">
                        Я даю согласие на обработку моих персональных данных в&nbsp;соответствии с&nbsp;<a href="/o-nas/politika-obrabotki-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer">Политикой обработки персональных данных</a> и&nbsp;даю <a href="/o-nas/soglasie-na-obrabotku-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer">Согласие на&nbsp;обработку персональных данных</a>
                    </span>
                </label>
            </div>
        </div>
        {#if consentError}<span class="field-error consent-error">{consentError}</span>{/if}

        <div class="captcha-cell">
            <Captcha sitekey={$form.captchaSitekey} bind:token={captchaToken} on:token={onCaptchaToken}/>
            {#if captchaError}<span class="field-error captcha-error">{captchaError}</span>{/if}
        </div>

        <button type="submit"
                class="button button-primary form-submit"
                disabled={isLoading || !consent}>Отправить</button>
    </div>
</form>

<style>
    .form-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .form-submit {
        margin-top: 20px;
        grid-column: 1 / -1;
        justify-self: center;
        font-family: Ubuntu, Arial, Helvetica, sans-serif;
        font-size: 10px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        padding: 10px 30px 9px;
    }

    .form-submit:disabled {
        opacity: 0.6;
        cursor: progress;
    }

    .required {
        color: var(--red);
    }

    .field-cell .field-error,
    .consent-error.field-error {
        color: var(--red);
        font-size: 12px;
        line-height: 1.3;
        display: block;
        margin-top: 6px;
    }

    .field-cell {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .consent-error {
        grid-column: 1 / -1;
        margin-top: 4px;
    }

    .has-error {
        border-color: var(--red);
    }

    /* The form is mounted into the product card's .like_icons.iblock, where Aspro sets
       line-height: 0 (template_styles.css). svelte-simple-modal renders the window in
       place, so the zeroed leading is inherited by the whole modal. Break the
       inheritance once, at the mount point, without cancelling the template rule. */
    :global(#form-app-item-ask) {
        line-height: 1.45;
    }

    /* Aspro sizes every <span> inside the card's icon row directly
       (.catalog_detail .top_info .like_icons span { font-size: 11px }), so the consent
       text is hit by the rule itself rather than by inheritance and the mount-point rule
       above cannot help. Reclaim the label's 13px. The id selector is what outranks
       Aspro's three-class selector; keep it targeted at .privacy-consent-text, since a
       blanket span rule would also outrank the scoped .field-error size. */
    :global(#form-app-item-ask .privacy-consent-text) {
        font-size: inherit;
    }

    /* Consent block relies on the site-wide .privacy-consent-* classes from the global
       styles.css; only the full-row grid span is specific to this form. */
    .form-consent {
        grid-column: 1 / -1;
    }

    /* Same split as the consent block: the widget's own look comes from the global
       .contact-method-* rules, and only its placement in this grid belongs here. */
    .contact-method-cell {
        grid-column: 1 / -1;
    }

    .captcha-cell {
        grid-column: 1 / -1;
    }

    .captcha-error.field-error {
        color: var(--red);
        font-size: 12px;
        line-height: 1.3;
        display: block;
        margin-top: 6px;
    }
</style>
